import { Box, Card, Flex, GridItem, IconButton, SimpleGrid, Spacer, Text } from "@chakra-ui/react";
import { isDesktop } from 'react-device-detect';
import { ColorModeSwitcher } from "../ColorModeSwitcher";
import { LanguageSwitcher } from "./LanguageSwitcher";
import Icon, { Icons } from "./Icons";
import { forwardRef, useContext, useState } from "react";
import { Context } from "../App";
import { useNavigate } from "react-router-dom";
import { RoutePath, SPACING } from "../generic/config";
import { useAuth } from "react-oidc-context";
import { setSelectedInstanceId } from "../generic/authStore";
import useToast from "./hooks/useToast";
import Tooltip from "./Tooltip";
import Drawer from "@/generic/Drawer";
import { PopoverCloseTrigger, PopoverContent, PopoverRoot, PopoverTrigger } from "@/components/ui/popover";
import usePalette from "./hooks/usePalette";
import { QueryKeys } from "@/api/base/react-query";
import { GetInstances } from "@/api/authentication-controller";
import { useQuery } from "react-query";

const Header = forwardRef<HTMLDivElement, {}>((props, ref) => {
    const { background } = usePalette();
    const { username, translations, companySettings, } = useContext(Context);
    const navigate = useNavigate();
    const auth = useAuth();
    const toast = useToast();
    const instances = useQuery(QueryKeys.instances, GetInstances, { enabled: !!username });
    const hasMultipleInstances = (instances.data?.length ?? 0) > 1;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    if (isDesktop) {
        return (
            <Card.Root ref={ref} appearance="dark" position={"fixed"} left={0} right={0} zIndex={99} rounded={"initial"} shadow="xs">
                <Card.Body p={0}>
                    <SimpleGrid columns={3}>
                        <GridItem>
                            <Flex>
                                <Text fontSize="lg" fontWeight="700" mt={SPACING / 2} ml={SPACING} letterSpacing="-0.025em">
                                    Code F
                                </Text>
                                <Text fontSize="small" mt={SPACING} ml={SPACING}>
                                    Work order management system
                                </Text>
                            </Flex>
                        </GridItem>
                        <GridItem>
                            <Tooltip tooltip={translations.installation.currentInstallation} >
                                <Text textAlign="center" fontSize="lg" mt={SPACING / 2}>
                                    {username ? companySettings?.data?.name : ""}
                                </Text>
                            </Tooltip>
                        </GridItem>
                        <GridItem>
                            <Flex justifyContent="flex-end" alignItems="center" height="fit-content" >
                                <Spacer />
                                <Tooltip tooltip={translations.currentUser} >
                                    {username && (
                                        <Text fontSize="lg" marginRight="2">{username}</Text>
                                    )}
                                </Tooltip>
                                {hasMultipleInstances && (
                                    <Tooltip tooltip={translations.installation.changeInstallation} >
                                        <IconButton fontSize="lg" variant="ghost" aria-label="Installation" disabled={username === "" || window.location.href.includes(RoutePath.SelectInstance)} onClick={() => {
                                            navigate(RoutePath.SelectInstance);
                                        }
                                        }>
                                            <Icon icon={Icons.SwitchInstallation} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip tooltip={translations.switchTheme} > <ColorModeSwitcher /> </Tooltip>
                                <Tooltip tooltip={translations.switchLanguage} > <LanguageSwitcher /> </Tooltip>
                                <Tooltip tooltip={translations.signOut} >
                                    <IconButton fontSize="lg" variant="ghost" aria-label="Log out" disabled={username === ""} onClick={() => {
                                        setSelectedInstanceId(null); void auth.signoutRedirect();
                                        toast(translations.signedOut, "", "success");
                                    }}>
                                        <Icon icon={Icons.Logout} />
                                    </IconButton>
                                </Tooltip>
                            </Flex>
                        </GridItem>
                    </SimpleGrid>
                </Card.Body>
            </Card.Root>
        )
    }


    return (
        <Card.Root ref={ref} appearance="dark" position={"fixed"} left={0} right={0} zIndex={99} rounded={"initial"}>
            <Card.Body p={0}>
                <Flex ml={SPACING}>
                    <PopoverRoot open={isDrawerOpen} onOpenChange={e => setIsDrawerOpen(e.open)}>
                        <PopoverTrigger>
                            <Icon icon={Icons.menu} mt={-SPACING} />
                        </PopoverTrigger>
                        <PopoverContent shadow="none">
                            <Box bg={background.lightest} minH={"100vh"} minW="101vw" ml={-SPACING} mt={-SPACING * 6} overflow="hidden">
                                <Drawer ratioInPercent={50} onSelect={() => setIsDrawerOpen(false)} />
                            </Box>
                            <Box position="fixed" ml={-SPACING * 2} mt={-SPACING * 6} minH={"3ch"} minW="99vw" zIndex={100}>
                                <PopoverCloseTrigger />
                            </Box>
                        </PopoverContent>
                    </PopoverRoot>
                    <Flex>
                        <Text fontSize="md" fontWeight="bold" mt={SPACING / 2} ml={SPACING}>
                            Code F
                        </Text>
                        <Text fontSize="small" mt={SPACING} ml={SPACING}>
                            WOMS
                        </Text>
                    </Flex>
                    <Spacer />
                    {hasMultipleInstances && (
                        <Tooltip tooltip={translations.installation.changeInstallation} >
                            <IconButton fontSize="lg" variant="ghost" aria-label="Installation" disabled={username === "" || window.location.href.includes(RoutePath.SelectInstance)} onClick={() => {
                                navigate(RoutePath.SelectInstance);
                            }
                            }>
                                <Icon icon={Icons.SwitchInstallation} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip tooltip={translations.switchTheme} > <ColorModeSwitcher /> </Tooltip>
                    <Tooltip tooltip={translations.switchLanguage} > <LanguageSwitcher /> </Tooltip>
                    <Tooltip tooltip={translations.signOut} >
                        <IconButton fontSize="lg" variant="ghost" aria-label="Log out" disabled={username === ""} onClick={() => {
                            setSelectedInstanceId(null); void auth.signoutRedirect();
                            toast(translations.signedOut, "", "success");
                        }}>
                            <Icon icon={Icons.Logout} />
                        </IconButton>
                    </Tooltip>
                </Flex>
                <Flex mt={-SPACING}>
                    <Tooltip tooltip={translations.installation.currentInstallation} >
                        <Text fontSize="small" ml={SPACING}>
                            {username ? companySettings?.data?.name : ""}
                        </Text>
                    </Tooltip>
                    <Spacer />
                    <Tooltip tooltip={translations.currentUser} >
                        {username && (
                            <Text fontSize="small" marginRight="2" ml={SPACING * 2}>{username}</Text>
                        )}
                    </Tooltip>
                </Flex>
            </Card.Body >
        </Card.Root >
    )
});

export default Header;